/**
 * Componente de filtros para envíos
 * Sigue el patrón establecido en peticiones
 */
import React, { useState, useMemo } from 'react';
import {
    Box,
    TextField,
    Button,
    Stack,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    InputAdornment,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Badge,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { BOTON_PRIMARIO_SX, BOTON_SECUNDARIO_SX, ALTURA_BOTON } from '../../../../components/ui/buttonStyles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

const FiltrosEnvios = ({
    fechaInicio,
    setFechaInicio,
    fechaFin,
    setFechaFin,
    serieNumero,
    setSerieNumero,
    agencia,
    setAgencia,
    agenciasDisponibles = [],
    onBuscar,
    onBuscarPendientes,
    onLimpiar,
    loading,
    totalResultados,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
    const [expanded, setExpanded] = useState(true);

    // Contador de filtros activos
    const filtrosActivos = useMemo(() => {
        let count = 0;
        if (fechaInicio) count++;
        if (fechaFin) count++;
        if (serieNumero) count++;
        if (agencia) count++;
        return count;
    }, [fechaInicio, fechaFin, serieNumero, agencia]);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !loading) {
            onBuscar();
        }
    };

    const filtrosContent = (
        <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'flex-end' }}
        >
            {/* Número de albarán */}
            <Box sx={{ flex: 1 }}>
                <TextField
                    label="Nº albarán"
                    placeholder="01-12345"
                    value={serieNumero}
                    onChange={(e) => setSerieNumero(e.target.value)}
                    onKeyPress={handleKeyPress}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#6c757d' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                        },
                    }}
                />
            </Box>

            {/* Fecha inicio */}
            <Box sx={{ flex: 1 }}>
                <TextField
                    type="date"
                    label="Fecha inicio"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    onKeyPress={handleKeyPress}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                        },
                    }}
                />
            </Box>

            {/* Fecha fin */}
            <Box sx={{ flex: 1 }}>
                <TextField
                    type="date"
                    label="Fecha fin"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    onKeyPress={handleKeyPress}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={fechaFin && fechaInicio && fechaFin < fechaInicio}
                    helperText={
                        fechaFin && fechaInicio && fechaFin < fechaInicio
                            ? 'Debe ser posterior a fecha inicio'
                            : ''
                    }
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                        },
                    }}
                />
            </Box>

            {/* Agencia */}
            <Box sx={{ flex: 1 }}>
                <FormControl fullWidth>
                    <InputLabel shrink>Agencia</InputLabel>
                    <Select
                        displayEmpty
                        notched
                        value={agencia}
                        onChange={(e) => setAgencia(e.target.value)}
                        label="Agencia"
                        sx={{
                            backgroundColor: '#fff',
                        }}
                    >
                        <MenuItem value="">Todas</MenuItem>
                        {agenciasDisponibles.map((ag) => (
                            <MenuItem key={ag} value={ag}>
                                {ag}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Botón Pendientes */}
            <Box sx={{ flex: { xs: 1, md: '0 0 auto' }, width: { md: 170 } }}>
                <Button
                    variant="contained"
                    onClick={onBuscarPendientes}
                    disabled={loading}
                    fullWidth
                    startIcon={<PendingActionsIcon />}
                    sx={{
                        ...BOTON_PRIMARIO_SX,
                        height: ALTURA_BOTON.FILTRO,
                        backgroundColor: '#ffc107',
                        color: '#000',
                        '&:hover': {
                            backgroundColor: '#e0a800',
                        },
                        '&:disabled': {
                            backgroundColor: '#6c757d',
                            color: '#fff',
                        },
                    }}
                    title="Buscar y mostrar solo pendientes"
                >
                    Pendientes
                </Button>
            </Box>

            {/* Botón Buscar/Filtrar */}
            <Box sx={{ flex: { xs: 1, md: '0 0 auto' }, width: { md: 140 } }}>
                <Button
                    variant="contained"
                    onClick={onBuscar}
                    disabled={loading}
                    fullWidth
                    startIcon={
                        loading ? (
                            <CircularProgress size={18} sx={{ color: '#fff' }} />
                        ) : (
                            <Badge badgeContent={filtrosActivos} color="error">
                                <FilterListIcon />
                            </Badge>
                        )
                    }
                    sx={{ ...BOTON_PRIMARIO_SX, height: ALTURA_BOTON.FILTRO }}
                >
                    Filtrar
                </Button>
            </Box>

            {/* Botón Limpiar */}
            <Box sx={{ flex: { xs: 1, md: '0 0 auto' }, width: { md: 140 } }}>
                <Button
                    variant="outlined"
                    onClick={onLimpiar}
                    disabled={loading}
                    fullWidth
                    startIcon={<ClearIcon />}
                    sx={{ ...BOTON_SECUNDARIO_SX, height: ALTURA_BOTON.FILTRO }}
                >
                    Limpiar
                </Button>
            </Box>
        </Stack>
    );

    // En mobile: Accordion colapsable
    if (isMobile) {
        return (
            <Box>
                <Accordion
                    expanded={expanded}
                    onChange={() => setExpanded(!expanded)}
                    sx={{
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        borderRadius: '8px !important',
                        '&:before': { display: 'none' },
                        mb: 2,
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: expanded ? '8px 8px 0 0' : '8px',
                            '& .MuiAccordionSummary-content': {
                                alignItems: 'center',
                                gap: 1,
                            },
                        }}
                    >
                        <FilterListIcon sx={{ color: '#cc071e' }} />
                        <Typography sx={{ fontWeight: 600, flex: 1 }}>Filtros</Typography>
                        {filtrosActivos > 0 && (
                            <Badge badgeContent={filtrosActivos} color="error" sx={{ mr: 2 }} />
                        )}
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 3 }}>
                        {filtrosContent}
                        {totalResultados !== undefined && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 2, textAlign: 'center' }}
                            >
                                {totalResultados} {totalResultados === 1 ? 'resultado' : 'resultados'}
                            </Typography>
                        )}
                    </AccordionDetails>
                </Accordion>
            </Box>
        );
    }

    // En desktop: Siempre visible
    return (
        <Box>
            {filtrosContent}
            {totalResultados !== undefined && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2, textAlign: 'right' }}
                >
                    {totalResultados} {totalResultados === 1 ? 'resultado' : 'resultados'}
                </Typography>
            )}
        </Box>
    );
};

export default FiltrosEnvios;
